import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	effect,
	inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import {
	VehicleActionEnum,
	vehicleValidators,
} from '@splitsecondconnect/ultramarine-shared';

import { BaseActionComponent } from '../../../../../shared/base-classes/base-action.component';
import { ActionPortalContainerComponent } from '../../../../../shared/components/containers/action-portal-container/action-portal-container.component';
import { SectionWrapperComponent } from '../../../../../shared/components/containers/section-wrapper-container/section-wrapper-container.component';
import { DatetimePickerComponent } from '../../../../../shared/components/datetime-picker/datetime-picker.component';
import { InputFieldComponent } from '../../../../../shared/components/input-field/input-field.component';
import { MoreSectionsComponent } from '../../../../../shared/components/more-sections/more-sections.component';
import {
	dateCast,
	toDatetimeLocalString,
} from '../../../../../shared/functions/sectionCasters.function';
import { VehiclesService } from '../../vehicles.service';

function optionalNumber(value: unknown): number | undefined {
	if (value === null || value === undefined || value === '') {
		return undefined;
	}

	const parsed = Number(value);

	return Number.isNaN(parsed) ? undefined : parsed;
}

@Component({
	selector: 'app-sell-vehicle-task',
	templateUrl: 'sell-vehicle-task.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	host: {
		'[class]': 'modalClass()',
	},
	imports: [
		InputFieldComponent,
		DatetimePickerComponent,
		ActionPortalContainerComponent,
		SectionWrapperComponent,
		MoreSectionsComponent,
		ReactiveFormsModule,
	],
})
export class SellVehicleTaskComponent extends BaseActionComponent {
	private _vehiclesService = inject(VehiclesService);

	public taskFormGroup = this._formBuilder.group({
		soldAt: [toDatetimeLocalString(new Date())],
		saleAskPrice: [null as number | null],
		saleSoldPrice: [null as number | null],
		purchasedAt: [null as string | null],
		purchaseListPrice: [null as number | null],
		purchasePaidPrice: [null as number | null],
	});

	constructor(
		private _formBuilder: FormBuilder,
		private _cdr: ChangeDetectorRef,
	) {
		super();

		effect(() => {
			const rd = this.recordData();

			if (!rd) {
				return;
			}

			this.taskFormGroup.patchValue({
				purchasedAt: rd.purchasedAt
					? toDatetimeLocalString(new Date(rd.purchasedAt))
					: null,
				purchaseListPrice: rd.purchaseListPrice ?? null,
				purchasePaidPrice: rd.purchasePaidPrice ?? null,
			});
			this._cdr.markForCheck();
		});

		this.taskFormGroup.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => {
			this.errorMessages.set(null);
		});
	}

	public submitTask(): void {
		const rd = this.recordData();

		const validation = vehicleValidators.canPerformAction(
			rd,
			VehicleActionEnum.SELL,
		);

		if (!validation.isValid) {
			this.errorMessages.set(validation.errors);
			this._cdr.markForCheck();

			return;
		}

		const v = this.taskFormGroup.getRawValue();
		const vehicleId = this.selectionIds[0];

		const body = {
			soldAt: dateCast(v.soldAt) ?? undefined,
			saleAskPrice: optionalNumber(v.saleAskPrice),
			saleSoldPrice: optionalNumber(v.saleSoldPrice),
			purchasedAt: dateCast(v.purchasedAt) ?? undefined,
			purchaseListPrice: optionalNumber(v.purchaseListPrice),
			purchasePaidPrice: optionalNumber(v.purchasePaidPrice),
		};

		this.isSaving = true;
		this._vehiclesService.sell(vehicleId, body).subscribe({
			next: () => {
				this.successfullySubmittedTask$.next(null);
				this.onSuccess.next(null);
				this.onClose.next(null);
				this.clearTask();
			},
			error: () => {
				this.isSaving = false;
				this._cdr.markForCheck();
			},
			complete: () => {
				this.isSaving = false;
				void this.nav().pop();
			},
		});
	}
}
